import * as React from 'react';
import { Dialog, DialogClose, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger, } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { useTranslation } from 'react-i18next';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { object, string } from 'yup';
import { TextField } from '@/components/text-field';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { Form } from '@/components/ui/form';
import { inviteMember } from '@/services/workspace';
import { toast } from 'sonner';
import { errorHandler } from '@/lib/utils';
const inviteMemberSchema = object({
    email: string().email().required(),
});
export function InviteMemberDialog({ enabled, workspace }) {
    const { t } = useTranslation();
    const queryClient = useQueryClient();
    const [open, setOpen] = React.useState(false);
    const form = useForm({
        resolver: yupResolver(inviteMemberSchema),
        defaultValues: {
            email: '',
        },
    });
    const { mutate, isPending } = useMutation({
        mutationFn: inviteMember,
        onSuccess: (data) => {
            toast.success(data.message);
            // The new invite shows up as a pending row in the
            // WorkspaceInvitesAdminTable, and (once accepted) bumps the
            // members count which gates the seat-cap lock — invalidate
            // both so the UI reflects the change without a page reload.
            queryClient.invalidateQueries({ queryKey: ['workspace-invites', workspace.publicId] });
            queryClient.invalidateQueries({ queryKey: ['workspace-members', workspace.publicId] });
            form.reset({ email: '' });
            setOpen(false);
        },
        onError: errorHandler,
    });
    const onSubmit = React.useCallback((data) => {
        mutate({ workspaceId: workspace.publicId, data });
    }, [mutate, workspace.publicId]);
    // Reset on close so re-opening always starts blank — without this, an
    // accidentally-typed email survives a Cancel.
    const handleOpenChange = React.useCallback((next) => {
        if (!next)
            form.reset({ email: '' });
        setOpen(next);
    }, [form]);
    if (!enabled) {
        return (<Button variant="outline" disabled>
                {t('invite_member.button')}
            </Button>);
    }
    return (<Dialog open={open} onOpenChange={handleOpenChange}>
            <DialogTrigger asChild>
                <Button>{t('invite_member.button')}</Button>
            </DialogTrigger>

            <DialogContent>
                <DialogHeader>
                    <DialogTitle>{t('invite_member.title')}</DialogTitle>
                    <DialogDescription>{t('invite_member.description')}</DialogDescription>
                </DialogHeader>

                <Form {...form}>
                    <TextField control={form.control} name="email" label={t('email')} required autoFocus placeholder="member@email.com" disabled={isPending}/>
                </Form>

                <DialogFooter>
                    <DialogClose asChild>
                        <Button variant="outline" disabled={isPending}>
                            {t('cancel')}
                        </Button>
                    </DialogClose>
                    <Button type="button" onClick={form.handleSubmit(onSubmit)} disabled={isPending}>
                        {t('send_invite')}
                    </Button>
                </DialogFooter>
            </DialogContent>
        </Dialog>);
}
